import { useState, useContext } from "react";
import {
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import { Language } from "../../../App";

export function CheckoutForm({ onPaymentSuccess }) {
  const { lang } = useContext(Language) || {};
  const checkoutState = useCheckoutElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkoutState.type !== "success") {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // confirm() will redirect for methods that require authentication (e.g. Klarna).
      // If it does not redirect, it resolves with a CheckoutConfirmationResult.
      const result = await checkoutState.checkout.confirm({});

      if (result?.type === "error") {
        setMessage(result.error?.message ?? (lang?.unexpected_error || "An unexpected error occurred."));
      } else if (result?.type === "session_finalized") {
        // Payment completed without a redirect (e.g. test cards, some APMs).
        if (onPaymentSuccess) {
          onPaymentSuccess(result.session);
        }
      } else if (result?.type === "session_updated") {
        // Session state changed but is not finalized yet. The UI remains usable
        // or Stripe will perform a redirect for required authentication.
        console.log("[Stripe] Checkout session updated:", result.session);
      } else {
        // Defensive fallback for any other resolved state.
        console.log("[Stripe] Checkout confirm result:", result);
      }
    } catch (error) {
      console.error("[Stripe] Checkout confirm error:", error);
      setMessage(
        error?.message ?? (lang?.unable_to_process_payment || "Unable to process the payment. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (checkoutState.type === "loading") {
    return <p className="text-sm text-gray-500">{lang?.loading_checkout || "Loading checkout…"}</p>;
  }

  if (checkoutState.type === "error") {
    return (
      <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
        {checkoutState.error.message}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-100 mx-auto space-y-4 text-left mt-3">
      <div className="rounded-lg border border-gray-200 p-4 shadow-sm min-h-[120px] mb-3">
        <PaymentElement
          options={{ layout: "tabs" }}
          onReady={() => console.log("[Stripe] PaymentElement ready")}
          onLoadError={(error) => console.error("[Stripe] PaymentElement load error:", error)}
        />
      </div>

      <button
        type="submit"
        disabled={isLoading || checkoutState.type !== "success"}
        className="btn btn-primary w-100"
        style={{ backgroundColor: "#1dc1dd", border: "none" }}
      >
        {isLoading ? (lang?.processing || "Processing…") : (lang?.pay || "Pay")}
      </button>

      {message && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 mt-3 text-danger">
          {message}
        </div>
      )}
    </form>
  );
}
